import { useCallback, useRef } from 'react'
import { DEFAULT_SHAPE_SIZES } from '@/lib/shapes/constants/default-shape-sizes'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { ShapeType } from '@/lib/shapes/types/shape-type'
import type { PathCommand } from '@/lib/elements/types/path-command'
import { pathCommandsForSides } from '@/lib/elements/shape-path-commands'
import {
  defaultInsetForType,
  defaultSidesForType,
  innerRatioFromInsetPercent,
  isSidesEligibleShapeType,
} from '@/lib/object-effects/sides'
import type { ShapeHistoryLifecycleMeta } from '@/lib/history/types/shape-history-lifecycle-meta'
import {
  applyPartialShapeUpdate,
  computeShapePosition,
  dedupeShapesByIdPreserveOrder,
  getShapeById,
} from '../helpers/shape-list'
import {
  applySelectionLockPolicy,
  normalizeSelectableIdsForScope,
} from '../helpers/selection-scope'
import type { ShapeStyleOverrides } from '@/lib/shapes/types/shape-style-overrides'
import type { ShapeManagerStateApi } from './use-shape-manager-state'
import type { EditScopeRefsApi } from './use-edit-scope-refs'

export function useShapeCollection(ctx: ShapeManagerStateApi & EditScopeRefsApi) {
  const {
    editingShapeTextIdRef,
    editScopeGroupIdRef,
    onShapeCreated,
    onShapeDeleted,
    onUserEdit,
    selectedShapeIds,
    selectedShapeIdsRef,
    setEditingShapeTextId,
    setSelectedShapeIds,
    setShapeBounds,
    setShapes,
    shapes,
    shapesRef,
  } = ctx

  const generateShapeId = useCallback(() => {
    // Backend requires a 32-character hex GUID (no dashes) for client-generated IDs.
    return crypto.randomUUID().replace(/-/g, '')
  }, [])

  const lastUsedStylesRef = useRef<ShapeStyleOverrides>({
    fill: '#e5e7eb',
    fillColorId: 2,
    fillTint: 20,
    stroke: '#000000',
    strokeWidth: 1,
  })

  const setLastUsedStyles = useCallback((overrides: Partial<ShapeStyleOverrides>) => {
    lastUsedStylesRef.current = { ...lastUsedStylesRef.current, ...overrides }
  }, [])

  const addShape = useCallback(
    (
      type: ShapeType,
      x: number,
      y: number,
      opts?: {
        width?: number
        height?: number
        anchor?: 'center' | 'top-left' | 'bottom-right'
        anchorPoint?: { x: number; y: number }
        pathCommands?: PathCommand[]
        path?: string
        styleOverrides?: ShapeStyleOverrides
        selectAfterCreate?: boolean
      }
    ) => {
      const defaults = DEFAULT_SHAPE_SIZES[type] ?? { width: 72, height: 72 }
      const width = opts?.width ?? defaults.width
      const height = opts?.height ?? defaults.height
      const anchor = opts?.anchor ?? 'center'
      const anchorPoint = opts?.anchorPoint

      const { left, top } = computeShapePosition(x, y, width, height, anchor, anchorPoint)

      const styles = opts?.styleOverrides
      const fill = styles?.fill ?? lastUsedStylesRef.current.fill ?? '#e5e7eb'
      const stroke = styles?.stroke ?? lastUsedStylesRef.current.stroke ?? '#000000'
      const strokeWidth = styles?.strokeWidth ?? lastUsedStylesRef.current.strokeWidth ?? 1
      const fillColorId = styles?.fillColorId ?? lastUsedStylesRef.current.fillColorId
      const fillTint = styles?.fillTint ?? lastUsedStylesRef.current.fillTint

      let pathCommands = opts?.pathCommands
      let sides: number | undefined
      let inset: number | undefined

      if (!pathCommands && isSidesEligibleShapeType(type)) {
        sides = defaultSidesForType(type)
        inset = defaultInsetForType(type)
        pathCommands = pathCommandsForSides(
          type,
          sides,
          width,
          height,
          innerRatioFromInsetPercent(inset)
        )
      }

      const newShape: ShapeData = {
        id: generateShapeId(),
        type,
        x: left,
        y: top,
        width,
        height,
        fill,
        ...(fillColorId != null ? { fillColorId } : {}),
        ...(fillTint != null ? { fillTint } : {}),
        stroke,
        strokeWidth,
        opacity: 1,
        ...(pathCommands ? { pathCommands } : {}),
        ...(opts?.path ? { path: opts.path } : {}),
        ...(sides !== undefined ? { sides } : {}),
        ...(inset !== undefined ? { inset } : {}),
      }

      lastUsedStylesRef.current = { fill, fillColorId, fillTint, stroke, strokeWidth }

      setShapes((prev) => {
        const next = [...prev, newShape]

        shapesRef.current = next
        return next
      })
      if (opts?.selectAfterCreate !== false) {
        setSelectedShapeIds([newShape.id])
      }

      onShapeCreated?.(newShape)
      return newShape
    },
    [generateShapeId, onShapeCreated, setSelectedShapeIds, setShapes, shapesRef]
  )

  const addShapeData = useCallback(
    (
      incoming: ShapeData,
      opts?: {
        selectAfterCreate?: boolean
        meta?: ShapeHistoryLifecycleMeta
        /**
         * Final size for a drag-sized creation, applied here rather than by the caller. A caller
         * that added the shape and then called `updateShape` was recording the PRE-resize
         * geometry in history, because `onShapeCreated` below takes the snapshot synchronously
         * and `updateShape` pushes no history action of its own — so redo re-created the shape at
         * the wrong size and re-persisted it (WMPCM-6884).
         */
        size?: { width: number; height: number }
      }
    ) => {
      const created: ShapeData = {
        ...incoming,
        id: incoming.id || generateShapeId(),
      }
      // Routed through the same `applyPartialShapeUpdate` that `updateShape` uses, so the path
      // rescale on a size change — `pathCommands` scaled, the SVG path regenerated, open/closed
      // state preserved for pen paths — lives in exactly one place. A clone of a polygon or pen
      // source therefore gets a correctly scaled path, not a source-sized one in a resized box.
      const newShape: ShapeData =
        opts?.size != null
          ? applyPartialShapeUpdate([created], created.id, {
              width: opts.size.width,
              height: opts.size.height,
            })[0]!
          : created

      // Advance the ref immediately (same reasoning as updateShape, ~line 2042): callers that
      // add several shapes back-to-back in one tick (e.g. clipboard paste re-creating a group
      // and its children, or Mirror Copy of a multi-selection) need shapesRef.current to
      // already include earlier additions when a later onShapeCreated callback (e.g. group
      // persistence) reads getShapes() — the setShapes functional updater alone runs on a
      // later render, too late for that.
      const next = [...shapesRef.current, newShape]

      shapesRef.current = next
      setShapes(next)

      if (opts?.selectAfterCreate !== false) {
        setSelectedShapeIds([newShape.id])
      }

      onShapeCreated?.(newShape, opts?.meta)
      return newShape
    },
    [generateShapeId, onShapeCreated, setSelectedShapeIds, setShapes, shapesRef]
  )

  const getLastUsedStyles = useCallback((): ShapeStyleOverrides => {
    return { ...lastUsedStylesRef.current }
  }, [])

  const loadShapes = useCallback(
    (incoming: ShapeData[], opts?: { clearExisting?: boolean }) => {
      const clearExisting = opts?.clearExisting ?? true

      setShapes((prev) => {
        let next: ShapeData[]

        if (clearExisting) {
          // Replace mode: preserve exact input ordering (history replay depends on this).
          next = dedupeShapesByIdPreserveOrder(incoming, 'loadShapes(replace)')
        } else {
          // Merge mode: keep existing order; new ids are appended by insertion order.
          const byId = new Map<string, ShapeData>()

          prev.forEach((s) => byId.set(s.id, s))
          incoming.forEach((s) => byId.set(s.id, { ...(byId.get(s.id) ?? {}), ...s }))
          next = Array.from(byId.values())
        }

        shapesRef.current = next
        return next
      })

      setShapeBounds({})

      // Keep selection if possible; otherwise clear. Always canonicalize to selectable roots.
      setSelectedShapeIds((prev) => {
        const source = clearExisting ? incoming : shapesRef.current
        const next = applySelectionLockPolicy(
          normalizeSelectableIdsForScope(
            prev.filter((id) => source.some((s) => s.id === id)),
            source,
            editScopeGroupIdRef.current
          ),
          source
        )

        selectedShapeIdsRef.current = next
        return next
      })
    },
    [
      editScopeGroupIdRef,
      selectedShapeIdsRef,
      setSelectedShapeIds,
      setShapeBounds,
      setShapes,
      shapesRef,
    ]
  )

  const removeShape = useCallback(
    (shapeId: string) => {
      onUserEdit?.([shapeId])
      setShapes((prev) => {
        const next = prev.filter((shape) => shape.id !== shapeId)

        shapesRef.current = next
        return next
      })
      setSelectedShapeIds((prev) => prev.filter((id) => id !== shapeId))
      setShapeBounds((prev) => {
        if (!(shapeId in prev)) {
          return prev
        }

        const next = { ...prev }

        delete next[shapeId]
        return next
      })
      // Mirrors TextManager's removeTextBox: removing the shape currently in shape-text edit
      // must drop the stale editing id, or the text editor overlay is left pointing at a shape
      // that no longer exists.
      if (editingShapeTextIdRef.current === shapeId) {
        editingShapeTextIdRef.current = null
        setEditingShapeTextId(null)
      }
    },
    [
      onUserEdit,
      editingShapeTextIdRef,
      setEditingShapeTextId,
      setSelectedShapeIds,
      setShapeBounds,
      setShapes,
      shapesRef,
    ]
  )

  const getShapes = useCallback(() => {
    return shapesRef.current
  }, [shapesRef])

  const clearShapes = useCallback(() => {
    setShapes(() => {
      shapesRef.current = []
      return []
    })
    setSelectedShapeIds([])
    setShapeBounds({})
  }, [setSelectedShapeIds, setShapeBounds, setShapes, shapesRef])

  const deleteSelectedShape = useCallback(
    (opts?: { historyTxnId?: string; restrictToIds?: readonly string[] }) => {
      // `restrictToIds` narrows the gesture to a subset of the selection without disturbing the
      // selection itself. Cut uses it to remove only the members it is allowed to remove while the
      // protected ones stay selected (WMPCM-8663); it must be an explicit argument rather than a
      // pre-call `selectShapes`, because the guards below read `selectedShapeIds` from render state
      // and would not observe a same-tick selection change. Absent, behavior is unchanged.
      const targetIds = opts?.restrictToIds ?? selectedShapeIds

      if (targetIds.length === 0) return

      const selectedRoots = targetIds
        .map((id) => getShapeById(shapes, id))
        .filter((shape): shape is ShapeData => shape != null)

      if (selectedRoots.some((shape) => shape.locked === true)) return
      if (selectedRoots.some((shape) => shape.frozen === true)) return

      const selectedSet = new Set(targetIds.filter((id) => getShapeById(shapes, id)))
      const shouldDelete = (shape: ShapeData): boolean => {
        if (selectedSet.has(shape.id)) return true
        let parentId = shape.parentGroupId

        while (parentId) {
          if (selectedSet.has(parentId)) return true
          parentId = getShapeById(shapes, parentId)?.parentGroupId
        }

        return false
      }
      const toDelete = shapes.filter((shape) => shouldDelete(shape))
      // Group descendants come out via `shouldDelete` too, so this is the full removed set — not
      // just `targetIds` — which is what the restricted-selection rebuild below has to subtract.
      const deletedIds = new Set(toDelete.map((shape) => shape.id))

      toDelete.forEach((shape) => {
        const meta = opts?.historyTxnId
          ? {
              historyTxnId: opts.historyTxnId,
              historyInsertIndex: shapes.findIndex((s) => s.id === shape.id),
            }
          : undefined

        onShapeDeleted?.(shape, meta)
        removeShape(shape.id)
      })

      if (opts?.restrictToIds) {
        // Keep whatever this gesture was not allowed to touch selected, so the user can see what
        // survived. The ref is advanced too, because the restricted caller (Cut) continues to read
        // the selection synchronously after this returns.
        const nextSelection = selectedShapeIds.filter((id) => !deletedIds.has(id))

        selectedShapeIdsRef.current = nextSelection
        setSelectedShapeIds(nextSelection)
      } else {
        setSelectedShapeIds([])
      }
    },
    [
      selectedShapeIds,
      shapes,
      onShapeDeleted,
      removeShape,
      selectedShapeIdsRef,
      setSelectedShapeIds,
    ]
  )

  return {
    generateShapeId,
    lastUsedStylesRef,
    addShape,
    addShapeData,
    getLastUsedStyles,
    setLastUsedStyles,
    loadShapes,
    removeShape,
    getShapes,
    clearShapes,
    deleteSelectedShape,
  }
}

export type ShapeCollectionApi = ReturnType<typeof useShapeCollection>
